import { createRootRoute, createRoute, createRouter } from "@tanstack/react-router";

import { App } from "./App.js";
import { FeatureGate } from "./components/FeatureGate.js";
import { NotFound } from "./components/NotFound.js";
import { RouteError } from "./components/RouteError.js";
import { AccountPage } from "./pages/AccountPage.js";
import { BattleCreatePage } from "./pages/BattleCreatePage.js";
import { BattlePage } from "./pages/BattlePage.js";
import { BattlesPage } from "./pages/BattlesPage.js";
import { BoxDetailPage } from "./pages/BoxDetailPage.js";
import { BoxesPage } from "./pages/BoxesPage.js";
import { FairnessPage } from "./pages/FairnessPage.js";
import { ForgeCreatePage } from "./pages/ForgeCreatePage.js";
import { ForgeEditPage } from "./pages/ForgeEditPage.js";
import { ForgeMinePage } from "./pages/ForgeMinePage.js";
import { ForgePage } from "./pages/ForgePage.js";
import { HomePage } from "./pages/HomePage.js";
import { InventoryPage } from "./pages/InventoryPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { PrivacyPage } from "./pages/PrivacyPage.js";
import { TermsPage } from "./pages/TermsPage.js";
import { parseBoxesSearch } from "./templates/pages/boxes/content/search.js";

const rootRoute = createRootRoute({
  component: App,
  notFoundComponent: NotFound,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const boxesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/boxes",
  component: BoxesPage,
  // The tab and the filters, so a filtered grid survives opening a box and coming back.
  validateSearch: parseBoxesSearch,
});

const boxDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/boxes/$boxId",
  component: BoxDetailPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: AccountPage,
  // Stripe returns to ?purchase=success&session_id=... or ?purchase=cancelled (core/stripe/balancePurchases.ts).
  validateSearch: (
    search: Record<string, unknown>,
  ): { purchase?: string; session_id?: string } => ({
    purchase: typeof search.purchase === "string" && search.purchase ? search.purchase : undefined,
    session_id:
      typeof search.session_id === "string" && search.session_id ? search.session_id : undefined,
  }),
});

const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/inventory",
  component: InventoryPage,
});

const fairnessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/fairness",
  component: FairnessPage,
  // ?openId=<id> auto-verifies that open; the account page's history links here.
  validateSearch: (search: Record<string, unknown>): { openId?: string } => ({
    openId: typeof search.openId === "string" && search.openId ? search.openId : undefined,
  }),
});

// Battles: every route behind the tenant's switch (docs/battles.md).
const battlesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/battles",
  component: () => (
    <FeatureGate feature="battles">
      <BattlesPage />
    </FeatureGate>
  ),
});

const battleCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/battles/create",
  component: () => (
    <FeatureGate feature="battles">
      <BattleCreatePage />
    </FeatureGate>
  ),
  // ?boxId=<id> starts the picks with one round of that box; the box page's Battle link sets it.
  validateSearch: (search: Record<string, unknown>): { boxId?: string } => ({
    boxId: typeof search.boxId === "string" && search.boxId ? search.boxId : undefined,
  }),
});

const battleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/battles/$battleId",
  component: () => (
    <FeatureGate feature="battles">
      <BattlePage />
    </FeatureGate>
  ),
  // ?replay=1 re-runs a completed battle's choreography once.
  // The router parses `1` as a number, so accept either form.
  validateSearch: (search: Record<string, unknown>): { replay?: string } => ({
    replay:
      (typeof search.replay === "string" || typeof search.replay === "number") && search.replay
        ? String(search.replay)
        : undefined,
  }),
});

// The forge: every route behind the tenant's switch (docs/forge.md).
const forgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge",
  component: () => (
    <FeatureGate feature="forge">
      <ForgePage />
    </FeatureGate>
  ),
});

const forgeCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge/create",
  component: () => (
    <FeatureGate feature="forge">
      <ForgeCreatePage />
    </FeatureGate>
  ),
});

const forgeMineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge/mine",
  component: () => (
    <FeatureGate feature="forge">
      <ForgeMinePage />
    </FeatureGate>
  ),
});

const forgeEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forge/$boxId/edit",
  component: () => (
    <FeatureGate feature="forge">
      <ForgeEditPage />
    </FeatureGate>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  // ?error=invalid_link comes from the BFF's magic-link callback (apps/storefront/server/app.ts).
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: typeof search.error === "string" && search.error ? search.error : undefined,
  }),
});

// The policy pages: no feature gate, because a store cannot be without its terms.
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsPage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPage,
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  boxesRoute,
  boxDetailRoute,
  battlesRoute,
  battleCreateRoute,
  battleRoute,
  forgeRoute,
  forgeCreateRoute,
  forgeMineRoute,
  forgeEditRoute,
  accountRoute,
  inventoryRoute,
  fairnessRoute,
  loginRoute,
  termsRoute,
  privacyRoute,
]);

/** The storefront's route tree; mounted by main.tsx through RouterProvider. */
export const router = createRouter({ routeTree, defaultErrorComponent: RouteError });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
